import { useState, useCallback, useEffect, useRef } from 'react';
import { Bug, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type Status = 'idle' | 'sending' | 'success' | 'error' | 'rate_limited';

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Snapshot-getter för att bifoga rolling action log. */
  getSnapshot?: () => unknown;
  /** Workflow-stats (block/connector-counts) bifogas för kontext. */
  getWorkflowStats?: () => { blocks: number; connectors: number; groups: number };
}

const RATE_KEY = 'engicoro-bug-rate';
const CLIENT_MAX = 5;
const CLIENT_WINDOW_MS = 60 * 60 * 1000;

function checkClientRate(): { allowed: boolean; retryMinutes?: number } {
  try {
    const raw = localStorage.getItem(RATE_KEY);
    const now = Date.now();
    const state: { timestamps: number[] } = raw ? JSON.parse(raw) : { timestamps: [] };
    const active = state.timestamps.filter(t => now - t < CLIENT_WINDOW_MS);
    if (active.length >= CLIENT_MAX) {
      const oldest = Math.min(...active);
      return {
        allowed: false,
        retryMinutes: Math.ceil((CLIENT_WINDOW_MS - (now - oldest)) / 60_000),
      };
    }
    return { allowed: true };
  } catch {
    return { allowed: true };
  }
}

function recordSubmit() {
  try {
    const raw = localStorage.getItem(RATE_KEY);
    const state: { timestamps: number[] } = raw ? JSON.parse(raw) : { timestamps: [] };
    state.timestamps.push(Date.now());
    localStorage.setItem(RATE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export function BugReportDialog({
  open, onOpenChange, getSnapshot, getWorkflowStats,
}: BugReportDialogProps) {
  const isMobile = useIsMobile();
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const canSubmit =
    title.trim().length >= 5 &&
    description.trim().length >= 10 &&
    status !== 'sending';

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      setStatus('error');
      setMessage('Title (≥5 chars) and description (≥10 chars) required.');
      return;
    }
    const rate = checkClientRate();
    if (!rate.allowed) {
      setStatus('rate_limited');
      setMessage(`Too many reports. Try again in ${rate.retryMinutes} minutes.`);
      return;
    }

    setStatus('sending');
    setMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const url = `${supabaseUrl}/functions/v1/bug-report`;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          severity,
          steps_to_reproduce: stepsToReproduce.trim() || null,
          expected_behavior: expectedBehavior.trim() || null,
          actual_behavior: actualBehavior.trim() || null,
          metadata: {
            recent_actions: getSnapshot?.() ?? null,
            workflow_stats: getWorkflowStats?.() ?? null,
            user_agent: navigator.userAgent,
            viewport: { w: window.innerWidth, h: window.innerHeight },
          },
        }),
        signal: controller.signal,
      });

      const data = await resp.json();
      if (resp.ok && data.success) {
        recordSubmit();
        setStatus('success');
        setMessage(data.message || 'Bug report submitted. Thanks for helping us improve!');
        setTitle(''); setDescription('');
        setStepsToReproduce(''); setExpectedBehavior(''); setActualBehavior('');
      } else if (resp.status === 429) {
        setStatus('rate_limited');
        setMessage(data.message || 'Rate-limited. Try again later.');
      } else {
        setStatus('error');
        setMessage(data.message || data.error || 'Submission failed.');
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setStatus('error');
      setMessage('Could not reach the server.');
    }
  }, [canSubmit, title, severity, description, stepsToReproduce, expectedBehavior, actualBehavior, getSnapshot, getWorkflowStats]);

  const body = (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Bug className="w-3.5 h-3.5" />
        Your recent actions + workflow stats are attached automatically to help us debug.
      </div>

      <div>
        <Label className="text-xs">Title</Label>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Connectors vanish when I zoom out" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Severity</Label>
          <Select value={severity} onValueChange={v => setSeverity(v as Severity)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Description</Label>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} />
      </div>

      <div>
        <Label className="text-xs">Steps to reproduce (optional)</Label>
        <Textarea value={stepsToReproduce} onChange={e => setStepsToReproduce(e.target.value)} rows={2} placeholder="1. Click ...&#10;2. Drag ..." />
      </div>

      <div className="grid grid-cols-1 gap-2">
        <div>
          <Label className="text-xs">Expected behavior (optional)</Label>
          <Textarea value={expectedBehavior} onChange={e => setExpectedBehavior(e.target.value)} rows={2} />
        </div>
        <div>
          <Label className="text-xs">Actual behavior (optional)</Label>
          <Textarea value={actualBehavior} onChange={e => setActualBehavior(e.target.value)} rows={2} />
        </div>
      </div>

      {status === 'error' && (
        <div className="flex gap-2 text-xs text-destructive items-start">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{message}</span>
        </div>
      )}
      {status === 'rate_limited' && (
        <div className="flex gap-2 text-xs text-orange-600 items-start">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{message}</span>
        </div>
      )}
      {status === 'success' && (
        <div className="flex gap-2 text-xs text-green-600 items-start">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{message}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
          <X className="w-4 h-4 mr-1" /> Close
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
          {status === 'sending' ? 'Sending…' : 'Submit bug report'}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetTitle>Report a bug</SheetTitle>
          <div className="mt-3">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-4 h-4" /> Report a bug
          </DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
